import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import DateInput from "@/components/ui/date-input";

const empty = {
  name: "", address: "", country: "", vat_trn: "",
  concerned_person_name: "", trade_license_number: "",
  licensing_authority: "", email: "", trade_license_expiry: "", aml_status: "",
};

export default function ClientForm({ open, onClose, initial, onSaved, defaultName }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          name: initial.name || "",
          address: initial.address || "",
          country: initial.country || "",
          vat_trn: initial.vat_trn || "",
          concerned_person_name: initial.concerned_person_name || "",
          trade_license_number: initial.trade_license_number || "",
          licensing_authority: initial.licensing_authority || "",
          email: initial.email || "",
          trade_license_expiry: (initial.trade_license_expiry || "").slice(0, 10),
          aml_status: initial.aml_status || "",
        });
      } else {
        setForm({ ...empty, name: defaultName || "" });
      }
    }
  }, [open, initial, defaultName]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      let saved;
      if (initial) {
        const { data } = await api.patch(`/clients/${initial.id}`, form);
        saved = data;
        toast.success("Client updated");
      } else {
        const { data } = await api.post("/clients", form);
        saved = data;
        toast.success("Client added");
      }
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const F = ({ k, label, mono }) => (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-600">{label}</Label>
      <Input
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className={"mt-1.5" + (mono ? " font-mono-num" : "")}
        data-testid={`client-${k}`}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display" data-testid="client-form-title">
            {initial ? "Edit Client" : "New Client"}
          </DialogTitle>
          <DialogDescription>All fields except Name are optional.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="client-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-600">Name <span className="text-red-600">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="client-name" />
            </div>
            <F k="country" label="Country" />
            <F k="vat_trn" label="VAT TRN" mono />
            <F k="concerned_person_name" label="Concerned Person Name" />
            <F k="email" label="Email" />
            <F k="trade_license_number" label="Trade License Number" mono />
            <F k="licensing_authority" label="Jurisdiction" />
            <F k="aml_status" label="AML Status" />
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-600">
                Trade License Expiry <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
              </Label>
              <div className="mt-1.5">
                <DateInput
                  value={form.trade_license_expiry}
                  onChange={(v) => setForm({ ...form, trade_license_expiry: v })}
                  testid="client-trade_license_expiry"
                />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Address</Label>
            <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="mt-1.5" data-testid="client-address" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="client-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {initial ? "Save changes" : "Create client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
